import { useState, useRef, useEffect } from 'react'
import { useAppStore } from '../../store/app'
import type { ChatRoom } from '../../store/types'
import { joinChatRoom, leaveChatRoom, renameChatRoom, deleteChatRoom, setChatNotifPrefs } from '../../api/ws'
import '../ActionMenu.css'
import './ChatRoomRow.css'

export function relativeTime(iso?: string | null): string {
  if (!iso) return ''
  const diff = Date.now() - Date.parse(iso)
  if (diff < 60_000) return 'just now'
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`
  return `${Math.floor(diff / 86_400_000)}d ago`
}

export function RoomRow({
  room,
  unread,
  unreadThreads = 0,
  muted,
  dimmed,
  onTap,
  onLongPress,
}: {
  room: ChatRoom
  unread: number
  // Threads in this room you follow that have new replies. Thread replies don't
  // count toward the room's own unread (Slack's rule), so without this a room
  // whose only news is in a thread looked read — while the badge said otherwise.
  unreadThreads?: number
  muted: boolean
  dimmed?: boolean
  onTap: () => void
  onLongPress: () => void
}) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const firedRef = useRef(false)

  function handlePointerDown() {
    firedRef.current = false
    timerRef.current = setTimeout(() => {
      firedRef.current = true
      onLongPress()
    }, 500)
  }

  function handlePointerUp() {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  function handlePointerCancel() {
    handlePointerUp()
  }

  function handleClick() {
    if (!firedRef.current) onTap()
  }

  const relTime = relativeTime(room.last_activity_at)

  return (
    <button
      className={`chat-list__row${dimmed ? ' chat-list__row--dimmed' : ''}${unread > 0 || unreadThreads > 0 ? ' chat-list__row--unread' : ''}`}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onClick={handleClick}
      aria-label={room.name}
    >
      <span className="chat-list__row-name">{room.name}</span>
      <span className="chat-list__row-meta">
        {muted && <span className="chat-list__row-muted">🔕</span>}
        {relTime && <span className="chat-list__row-time">{relTime}</span>}
        {unreadThreads > 0 && (
          <span
            className="chat-list__row-threads"
            aria-label={`${unreadThreads} thread${unreadThreads === 1 ? '' : 's'} with new replies`}
          >
            🧵{unreadThreads > 1 ? ` ${unreadThreads}` : ''}
          </span>
        )}
        {unread > 0 && <span className="chat-list__row-badge">{unread}</span>}
      </span>
    </button>
  )
}

export function RoomActionMenu({
  room,
  onClose,
  onNavigate,
}: {
  room: ChatRoom
  onClose: () => void
  onNavigate: () => void
}) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState(room.name)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const chatNotifModes = useAppStore((s) => s.chatNotifModes)
  const storeChatNotifMode = useAppStore((s) => s.setChatNotifMode)

  const muted = chatNotifModes[room.id] === 'muted'
  const isOwner = room.role === 'owner'
  const isMember = room.is_member

  async function handleJoin() {
    setBusy(true)
    setError(null)
    try {
      const r = await joinChatRoom(room.id)
      if (r.error) { setError(r.error); setBusy(false); return }
      onClose()
      onNavigate()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to join')
      setBusy(false)
    }
  }

  async function handleQuit() {
    setBusy(true)
    setError(null)
    try {
      const r = await leaveChatRoom(room.id)
      if (r.error) { setError(r.error); setBusy(false); return }
      onClose()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to leave')
      setBusy(false)
    }
  }

  function handleToggleMute() {
    const nextMode = muted ? 'all' : 'muted'
    storeChatNotifMode(room.id, nextMode)
    onClose()
    setChatNotifPrefs(room.id, nextMode).catch(() => {
      storeChatNotifMode(room.id, muted ? 'muted' : 'all')
    })
  }

  async function handleRename() {
    const name = renameValue.trim()
    if (!name || name === room.name) { setRenaming(false); return }
    setBusy(true)
    setError(null)
    try {
      const r = await renameChatRoom(room.id, name)
      if (r.error) { setError(r.error); setBusy(false); return }
      setRenaming(false)
      onClose()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to rename')
      setBusy(false)
    }
  }

  async function handleDelete() {
    if (!confirmDelete) { setConfirmDelete(true); return }
    setBusy(true)
    setError(null)
    try {
      const r = await deleteChatRoom(room.id)
      if (r.error) { setError(r.error); setBusy(false); return }
      onClose()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to delete')
      setBusy(false)
    }
  }

  useEffect(() => { setConfirmDelete(false) }, [room.id])

  return (
    <div
      className="msg-action-menu"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="msg-action-menu__panel">
        <div className="chat-list__menu-title">{room.name}</div>

        {error && <div className="chat-list__menu-error">{error}</div>}

        {renaming ? (
          <div className="chat-list__menu-rename">
            <input
              className="chat-list__menu-rename-input"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleRename() }}
              autoFocus
            />
            <button className="msg-action-menu__action" onClick={handleRename} disabled={busy}>
              <span className="msg-action-menu__action-icon">✓</span>
              {busy ? 'Saving…' : 'Save Name'}
            </button>
            <button className="msg-action-menu__action" onClick={() => setRenaming(false)}>
              <span className="msg-action-menu__action-icon">✕</span>
              Cancel
            </button>
          </div>
        ) : (
          <>
            {!isMember && (
              <button className="msg-action-menu__action" onClick={handleJoin} disabled={busy}>
                <span className="msg-action-menu__action-icon">👋</span>
                {busy ? 'Joining…' : 'Join Room'}
              </button>
            )}

            {isMember && (
              <button className="msg-action-menu__action" onClick={handleToggleMute}>
                <span className="msg-action-menu__action-icon">{muted ? '🔔' : '🔕'}</span>
                {muted ? 'Unmute' : 'Mute Notifications'}
              </button>
            )}

            {isOwner && (
              <button className="msg-action-menu__action" onClick={() => setRenaming(true)}>
                <span className="msg-action-menu__action-icon">✏️</span>
                Rename Room
              </button>
            )}

            {isMember && (
              <button
                className="msg-action-menu__action msg-action-menu__action--danger"
                onClick={handleQuit}
                disabled={busy}
              >
                <span className="msg-action-menu__action-icon">🚪</span>
                {busy ? 'Leaving…' : 'Quit Room'}
              </button>
            )}

            {isOwner && !room.is_default && (
              <button
                className={`msg-action-menu__action msg-action-menu__action--danger${confirmDelete ? ' msg-action-menu__action--confirming' : ''}`}
                onClick={handleDelete}
                disabled={busy}
              >
                <span className="msg-action-menu__action-icon">🗑️</span>
                {busy ? 'Deleting…' : confirmDelete ? 'Confirm Delete' : 'Delete Room'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
